import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabaseClient';
import {
  UPLOAD_IMAGE_BUCKET,
  uploadImageToSupabase,
  formatImageUploadError,
} from './uploadImage';
import { normalizePhotoUrls } from './ShopPhotosEditor';
import newlogo from './assets/newlogo.png';
import logo192 from './assets/logo192.png';
import mmjlogo from './assets/mmjlogo.png';
import logoPng from './assets/logo.png';
import './MediaAdminPage.css';

const ADS_BUCKET = process.env.REACT_APP_SUPABASE_ADS_BUCKET || 'ads-images';
const LIBRARY_BUCKETS = [UPLOAD_IMAGE_BUCKET, ADS_BUCKET];

const SOURCE_LABEL = {
  brand: 'Marka',
  public: 'PWA',
  storage: 'Storage',
  ads: 'Reklam',
  shops: 'Mağaza',
  calendar: 'Takvim',
};

const BRAND_ASSETS = [
  { id: 'brand-newlogo', name: 'Portal logo', usage: 'Admin kenar çubuğu', url: newlogo, sizeLabel: 'PNG', source: 'brand' },
  { id: 'brand-mmj', name: 'MMJ marka', usage: 'Uygulama kimliği', url: mmjlogo, sizeLabel: 'PNG', source: 'brand' },
  { id: 'brand-logo', name: 'Klasik logo', usage: 'Eski varlık', url: logoPng, sizeLabel: 'PNG', source: 'brand' },
  { id: 'brand-192', name: 'Uygulama ikonu 192', usage: 'PWA / bundle', url: logo192, sizeLabel: '192×192', source: 'brand' },
];

function formatBytes(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

function publicPath(file) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${base}/${file}`;
}

async function listBucketFiles(bucket, prefix = '', depth = 0) {
  if (depth > 6) return [];
  const { data, error } = await supabase.storage.from(bucket).list(prefix || undefined, {
    limit: 100,
    sortBy: { column: 'created_at', order: 'desc' },
  });
  if (error || !Array.isArray(data)) {
    if (error) throw error;
    return [];
  }

  const out = [];
  for (const item of data) {
    if (!item?.name || item.name === '.emptyFolderPlaceholder') continue;
    const path = prefix ? `${prefix}/${item.name}` : item.name;
    const isFolder = item.id == null;
    if (isFolder) {
      out.push(...await listBucketFiles(bucket, path, depth + 1));
      continue;
    }
    const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
    out.push({
      id: `${bucket}:${path}`,
      name: item.name,
      path,
      bucket,
      url: pub?.publicUrl || '',
      size: item.metadata?.size,
      updatedAt: item.updated_at || item.created_at || null,
      source: 'storage',
    });
  }
  return out;
}

function MediaCard({ item, onCopy, onDelete, copiedId, deletingId }) {
  const canDelete = item.source === 'storage' && item.bucket === UPLOAD_IMAGE_BUCKET;
  const [broken, setBroken] = useState(false);
  const source = SOURCE_LABEL[item.source] || item.source;

  return (
    <article className="media-card">
      <div className={`media-thumb${broken ? ' media-thumb--broken' : ''}`}>
        {broken ? (
          <span className="media-thumb-fallback">Görsel yok</span>
        ) : (
          <img src={item.url} alt="" onError={() => setBroken(true)} />
        )}
        <span className={`media-badge media-badge--${item.source || 'storage'}`}>{source}</span>
      </div>
      <div className="media-card-body">
        <h3 className="media-card-title" title={item.name}>{item.name}</h3>
        <p className="media-card-meta">
          {item.usage || item.bucket || 'Görsel'}
          {item.sizeLabel ? ` · ${item.sizeLabel}` : ''}
          {item.size ? ` · ${formatBytes(item.size)}` : ''}
        </p>
      </div>
      <div className="media-card-actions">
        <button type="button" className="media-btn" onClick={() => onCopy(item)}>
          {copiedId === item.id ? 'Kopyalandı' : 'Kopyala'}
        </button>
        <a className="media-btn media-btn--ghost" href={item.url} target="_blank" rel="noreferrer">
          Aç
        </a>
        {canDelete ? (
          <button
            type="button"
            className="media-btn media-btn--danger"
            disabled={deletingId === item.id}
            onClick={() => onDelete(item)}
          >
            {deletingId === item.id ? '…' : 'Sil'}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function MediaGrid({ items, empty, loading, ...cardProps }) {
  if (loading) {
    return (
      <div className="media-grid" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="media-card media-card--shimmer" />
        ))}
      </div>
    );
  }
  if (!items.length) return <p className="media-empty">{empty}</p>;
  return (
    <div className="media-grid">
      {items.map((item) => (
        <MediaCard key={item.id} item={item} {...cardProps} />
      ))}
    </div>
  );
}

export default function MediaAdminPage() {
  const fileRef = useRef(null);
  const [library, setLibrary] = useState([]);
  const [used, setUsed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [copiedId, setCopiedId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const pwaAssets = useMemo(() => [
    { id: 'pwa-192', name: 'logo192.png', usage: 'PWA manifest', url: publicPath('logo192.png'), sizeLabel: '192×192', source: 'public' },
    { id: 'pwa-512', name: 'logo512.png', usage: 'PWA manifest', url: publicPath('logo512.png'), sizeLabel: '512×512', source: 'public' },
    { id: 'pwa-favicon', name: 'favicon.ico', usage: 'Tarayıcı ikonu', url: publicPath('favicon.ico'), sizeLabel: 'ICO', source: 'public' },
  ], []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [storageLists, ads, shops, events] = await Promise.all([
        Promise.all(LIBRARY_BUCKETS.map((bucket) => (
          listBucketFiles(bucket).catch(() => [])
        ))),
        supabase.from('promotions').select('id, title, image_url'),
        supabase.from('shops').select('id, shop, photos'),
        supabase.from('app_calendar_events').select('id, title, image_url'),
      ]);

      setLibrary(storageLists.flat());

      const usedRows = [];
      (ads.data || []).forEach((row) => {
        if (!row?.image_url) return;
        usedRows.push({
          id: `ads:${row.id}`,
          name: row.title || 'Reklam',
          url: row.image_url,
          usage: 'Reklamlar',
          usedIn: row.title || '',
          source: 'ads',
        });
      });
      (shops.data || []).forEach((row) => {
        normalizePhotoUrls(row?.photos).forEach((url, idx) => {
          usedRows.push({
            id: `shop:${row.id}:${idx}`,
            name: row.shop || `Mağaza #${row.id}`,
            url,
            usage: 'Mağazalar',
            usedIn: row.shop || '',
            source: 'shops',
          });
        });
      });
      (events.data || []).forEach((row) => {
        if (!row?.image_url) return;
        usedRows.push({
          id: `cal:${row.id}`,
          name: row.title || 'Etkinlik',
          url: row.image_url,
          usage: 'Takvim',
          usedIn: row.title || '',
          source: 'calendar',
        });
      });
      setUsed(usedRows);

      const firstErr = ads.error || shops.error || events.error;
      if (firstErr) setError(firstErr.message);
    } catch (err) {
      setLibrary([]);
      setUsed([]);
      setError(formatImageUploadError(err, err?.bucket || UPLOAD_IMAGE_BUCKET));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await uploadImageToSupabase(file, { folder: 'media' });
      await load();
    } catch (err) {
      setError(formatImageUploadError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await uploadFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    await uploadFile(file);
  };

  const handleCopy = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      window.setTimeout(() => setCopiedId(''), 1800);
    } catch {
      setError('URL kopyalanamadı.');
    }
  };

  const handleDelete = async (item) => {
    if (!item?.path || item.bucket !== UPLOAD_IMAGE_BUCKET) return;
    if (!window.confirm(`${item.name} silinsin mi?`)) return;
    setDeletingId(item.id);
    setError('');
    try {
      const { error: delErr } = await supabase.storage.from(item.bucket).remove([item.path]);
      if (delErr) throw delErr;
      setLibrary((prev) => prev.filter((row) => row.id !== item.id));
    } catch (err) {
      setError(formatImageUploadError(err, item.bucket));
    } finally {
      setDeletingId('');
    }
  };

  const q = search.trim().toLowerCase();
  const match = (item) => {
    if (!q) return true;
    return [item.name, item.usage, item.bucket, item.usedIn, item.url]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const brand = [...BRAND_ASSETS, ...pwaAssets].filter(match);
  const storageItems = library.filter(match);
  const usedItems = used.filter(match);
  const cardProps = { onCopy: handleCopy, onDelete: handleDelete, copiedId, deletingId };

  const showBrand = filter === 'all' || filter === 'brand';
  const showLibrary = filter === 'all' || filter === 'library';
  const showUsed = filter === 'all' || filter === 'used';

  return (
    <div className="admin-page-content media-page">
      <div className="media-hero-actions">
        <button type="button" className="media-refresh" onClick={load} disabled={loading}>
          {loading ? 'Yükleniyor…' : 'Yenile'}
        </button>
        <button
          type="button"
          className="media-upload-btn"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Yükleniyor…' : 'Görsel yükle'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} />
      </div>

      {error ? <p className="media-alert" role="alert">{error}</p> : null}

      <div className="media-toolbar">
        <div className="media-search-wrap">
          <svg className="media-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="media-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ara…"
            aria-label="Medya ara"
          />
        </div>
        <div className="media-filters" role="tablist" aria-label="Filtre">
          {[
            { id: 'all', label: 'Tümü' },
            { id: 'brand', label: 'Marka' },
            { id: 'library', label: 'Yüklenen' },
            { id: 'used', label: 'Kullanım' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={filter === opt.id}
              className={`media-filter ${filter === opt.id ? 'is-active' : ''}`}
              onClick={() => setFilter(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="media-stats" aria-label="Özet">
          <div className="media-stat">
            <span className="media-stat-value">{library.length}</span>
            <span className="media-stat-label">Yüklenen</span>
          </div>
          <div className="media-stat">
            <span className="media-stat-value">{used.length}</span>
            <span className="media-stat-label">Kullanım</span>
          </div>
          <div className="media-stat">
            <span className="media-stat-value">{brand.length}</span>
            <span className="media-stat-label">Marka</span>
          </div>
        </div>
      </div>

      <div
        className={`media-dropzone${dragging ? ' is-dragging' : ''}${uploading ? ' is-busy' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <path d="M12 16V6M8 10l4-4 4 4" />
          <path d="M4 16.5V18a2 2 0 002 2h12a2 2 0 002-2v-1.5" />
        </svg>
        <div>
          <strong>{uploading ? 'Yükleniyor…' : 'Görsel sürükleyin veya seçin'}</strong>
          <span>PNG, JPG, WebP · en fazla 10 MB · `admin-images`</span>
        </div>
      </div>

      {showBrand ? (
        <section className="media-section">
          <h2 className="media-section-title">Marka &amp; PWA <em>{brand.length}</em></h2>
          <MediaGrid items={brand} empty="Marka varlığı bulunamadı." {...cardProps} />
        </section>
      ) : null}

      {showLibrary ? (
        <section className="media-section">
          <h2 className="media-section-title">Yüklenen görseller <em>{storageItems.length}</em></h2>
          <MediaGrid
            items={storageItems}
            loading={loading}
            empty="Henüz yüklenmiş görsel yok. Yukarıya sürükleyin veya “Görsel yükle”ye basın."
            {...cardProps}
          />
        </section>
      ) : null}

      {showUsed ? (
        <section className="media-section">
          <h2 className="media-section-title">Kullanıldığı yerler <em>{usedItems.length}</em></h2>
          <MediaGrid
            items={usedItems}
            empty="Reklam, mağaza veya takvim görseli yok."
            {...cardProps}
          />
        </section>
      ) : null}
    </div>
  );
}
